import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, ValidationError, SquadData } from '../../../types/league';

interface StepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

const SquadsStep: React.FC<StepProps> = ({ data, updateData, errors, addError, clearErrors }) => {
  const { t } = useTranslation();
  const [defaultOilPattern, setDefaultOilPattern] = useState<string>('');
  const [newSquad, setNewSquad] = useState<Partial<SquadData & { repeatWeekly: boolean }>>({
    name: '',
    date: '',
    start_time: '18:00',
    end_time: '21:00',
    games_per_squad: 3,
    repeatWeekly: false
  });

  const generateWeeklySquads = (baseSquad: SquadData, startDate: string, endDate: string): SquadData[] => {
    const squads: SquadData[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const baseDate = new Date(baseSquad.date);
    const dayOfWeek = baseDate.getDay();
    
    // Find the first occurrence of this day of week within the league period
    let current = new Date(start);
    while (current.getDay() !== dayOfWeek && current <= end) {
      current.setDate(current.getDate() + 1);
    }
    
    // Generate weekly squads
    while (current <= end) {
      squads.push({
        ...baseSquad,
        date: current.toISOString().split('T')[0],
        name: `${baseSquad.name} - Week ${squads.length + 1}`
      });
      current.setDate(current.getDate() + 7); // Add 7 days for next week
    }
    
    return squads;
  };

  const handleAddSquad = () => {
    if (!newSquad.name || !newSquad.date || !newSquad.start_time || !newSquad.end_time) {
      addError('squads', 'Please fill in all squad fields');
      return;
    }

    if (!newSquad.games_per_squad || newSquad.games_per_squad < 1 || newSquad.games_per_squad > 6) {
      addError('squads', 'Games per squad must be between 1 and 6');
      return;
    }

    // Validate time order
    if (newSquad.start_time && newSquad.end_time && newSquad.start_time >= newSquad.end_time) {
      addError('squads', 'End time must be after start time');
      return;
    }

    // Check if league dates are available for weekly repeat
    if (newSquad.repeatWeekly && (!data.startDate || !data.endDate)) {
      addError('squads', 'League start and end dates are required for weekly repeat. Please set them in Basic Information first.');
      return;
    }

    const existingSquads = data.squads || [];
    const baseSquad: SquadData = {
      name: newSquad.name!,
      date: newSquad.date!,
      start_time: newSquad.start_time!,
      end_time: newSquad.end_time!,
      games_per_squad: newSquad.games_per_squad || 3,
      oil_pattern: newSquad.oil_pattern || defaultOilPattern || undefined
    };

    let squadsToAdd: SquadData[];
    
    if (newSquad.repeatWeekly && data.startDate && data.endDate) {
      // Generate weekly squads for the entire league duration
      squadsToAdd = generateWeeklySquads(baseSquad, data.startDate, data.endDate);
    } else {
      // Add single squad
      squadsToAdd = [baseSquad];
    }

    updateData({
      squads: [...existingSquads, ...squadsToAdd]
    });

    // Reset form
    setNewSquad({
      name: '',
      date: '',
      start_time: '18:00',
      end_time: '21:00',
      games_per_squad: 3,
      repeatWeekly: false
    });
    clearErrors();
  };

  const handleRemoveSquad = (index: number) => {
    const squads = data.squads || [];
    const updatedSquads = squads.filter((_, i) => i !== index);
    updateData({ squads: updatedSquads });
  };

  const getFieldError = (field: string) => {
    return errors.find(e => e.field === field)?.message;
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getDayName = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.squadsStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.squadsStep.subtitle')}
        </p>
      </div>

      {/* Default Oil Pattern */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-2">{t('admin.createLeague.steps.squadsStep.oilPattern')}</h3>
        <p className="text-purple-300 text-sm mb-4">{t('admin.createLeague.steps.squadsStep.oilPatternHelp')}</p>
        <select
          value={defaultOilPattern}
          onChange={(e) => setDefaultOilPattern(e.target.value)}
          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="" className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.oilPatternOptions.select')}</option>
          <option value="House" className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.oilPatternOptions.house')}</option>
          <option value="Challenge" className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.oilPatternOptions.challenge')}</option>
          <option value="Sport" className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.oilPatternOptions.sport')}</option>
        </select>
      </div>

      {/* Existing Squads */}
      {data.squads && data.squads.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">{t('admin.createLeague.steps.squadsStep.currentSquads')}</h3>
          {data.squads.map((squad, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-4 border border-white/20">
              <div className="flex justify-between items-start">
                  <div className="flex-1">
                  <h4 className="font-semibold text-white">{squad.name}</h4>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-purple-300">
                    <span>{getDayName(squad.date)} ({squad.date})</span>
                    <span>{formatTime(squad.start_time)} - {formatTime(squad.end_time)}</span>
                    <span className="bg-purple-500/20 px-2 py-1 rounded text-purple-200">
                      {squad.games_per_squad} {squad.games_per_squad === 1 ? t('admin.createLeague.steps.squadsStep.gameSingular') : t('admin.createLeague.steps.squadsStep.gamePlural')}
                    </span>
                    {squad.oil_pattern && (
                      <span className="bg-blue-500/20 px-2 py-1 rounded text-blue-200">
                        🎳 {squad.oil_pattern}
                      </span>
                    )}
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs text-purple-300 mb-1">{t('admin.createLeague.steps.squadsStep.modifyOilPattern')}</label>
                    <select
                      value={squad.oil_pattern || ''}
                      onChange={(e) => {
                        const updatedSquads = [...(data.squads || [])];
                        updatedSquads[index] = { ...squad, oil_pattern: e.target.value || undefined };
                        updateData({ squads: updatedSquads });
                      }}
                      className="w-full px-3 py-1.5 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="" className="bg-gray-800">{defaultOilPattern || t('admin.createLeague.steps.squadsStep.oilPatternOptions.select')}</option>
                      <option value="House" className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.oilPatternOptions.house')}</option>
                      <option value="Challenge" className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.oilPatternOptions.challenge')}</option>
                      <option value="Sport" className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.oilPatternOptions.sport')}</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveSquad(index)}
                  className="text-red-400 hover:text-red-300 ml-4"
                >
                  {t('admin.createLeague.steps.squadsStep.remove')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Squad Form */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.squadsStep.addNewSquad')}</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.squadsStep.squadName')} *
            </label>
            <input
              type="text"
              value={newSquad.name}
              onChange={(e) => setNewSquad({ ...newSquad, name: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('admin.createLeague.steps.squadsStep.squadNamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.squadsStep.squadDay')} *
            </label>
            <input
              type="date"
              value={newSquad.date}
              onChange={(e) => setNewSquad({ ...newSquad, date: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-purple-300 text-sm mt-1">
              {t('admin.createLeague.steps.squadsStep.squadDayHelp')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.squadsStep.startTime')} *
            </label>
            <input
              type="time"
              value={newSquad.start_time}
              onChange={(e) => setNewSquad({ ...newSquad, start_time: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.squadsStep.endTime')} *
            </label>
            <input
              type="time"
              value={newSquad.end_time}
              onChange={(e) => setNewSquad({ ...newSquad, end_time: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.squadsStep.gamesPerSquad')} *
            </label>
            <select
              value={newSquad.games_per_squad || 3}
              onChange={(e) => setNewSquad({ ...newSquad, games_per_squad: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value={1} className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.gamesOptions.one')}</option>
              <option value={2} className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.gamesOptions.two')}</option>
              <option value={3} className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.gamesOptions.three')}</option>
              <option value={4} className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.gamesOptions.four')}</option>
              <option value={5} className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.gamesOptions.five')}</option>
              <option value={6} className="bg-gray-800">{t('admin.createLeague.steps.squadsStep.gamesOptions.six')}</option>
            </select>
            <p className="text-purple-300 text-sm mt-1">
              {t('admin.createLeague.steps.squadsStep.gamesPerSquadHelp')}
            </p>
          </div>
        </div>

        {/* Weekly Repeat Option */}
        <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="repeatWeekly"
              checked={newSquad.repeatWeekly}
              onChange={(e) => setNewSquad({ ...newSquad, repeatWeekly: e.target.checked })}
              className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500"
            />
            <label htmlFor="repeatWeekly" className="ml-2 text-purple-200 font-medium">
              {t('admin.createLeague.steps.squadsStep.repeatWeekly')}
            </label>
          </div>
          <p className="text-purple-300 text-sm mt-2 ml-6">
            {data.startDate && data.endDate ? (
              <>
                {t('admin.createLeague.steps.squadsStep.repeatWeeklyMessage', {
                  startDate: data.startDate,
                  endDate: data.endDate,
                  weeks: Math.ceil((new Date(data.endDate).getTime() - new Date(data.startDate).getTime()) / (7 * 24 * 60 * 60 * 1000))
                })}
              </>
            ) : (
              t('admin.createLeague.steps.squadsStep.repeatWeeklyNoDates')
            )}
          </p>
        </div>

        <button
          onClick={handleAddSquad}
          className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200"
        >
          {newSquad.repeatWeekly ? t('admin.createLeague.steps.squadsStep.generateWeekly') : t('admin.createLeague.steps.squadsStep.addSquad')}
        </button>

        {getFieldError('squads') && (
          <p className="text-red-400 text-sm mt-2">{getFieldError('squads')}</p>
        )}
      </div>

      {/* Squad Examples */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.squadsStep.commonSetups.title')}</h3>
        <div className="grid md:grid-cols-2 gap-4 text-purple-200 text-sm">
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.squadsStep.commonSetups.standard.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.standard.monday')}</li>
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.standard.thursdayEarly')}</li>
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.standard.thursdayLate')}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.squadsStep.commonSetups.tournament.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.tournament.morning')}</li>
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.tournament.afternoon')}</li>
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.tournament.evening')}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.squadsStep.commonSetups.senior.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.senior.tuesday')}</li>
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.senior.wednesday')}</li>
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.senior.friday')}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.squadsStep.commonSetups.championship.title')}</h4>
            <ul className="space-y-1">
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.championship.saturdayFinals')}</li>
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.championship.sundayChampionship')}</li>
              <li>• {t('admin.createLeague.steps.squadsStep.commonSetups.championship.holidaySpecial')}</li>
            </ul>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-600/20 border border-blue-400 rounded-lg">
          <h4 className="font-semibold text-blue-200 mb-2">💡 {t('admin.createLeague.steps.squadsStep.flexibilityNote.title')}</h4>
          <p className="text-blue-200 text-sm">
            {t('admin.createLeague.steps.squadsStep.flexibilityNote.description')}
          </p>
        </div>
      </div>

      {(!data.squads || data.squads.length === 0) && (
        <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4 text-center">
          <p className="text-yellow-200">
            💡 {t('admin.createLeague.steps.squadsStep.noSquadsWarning')}
          </p>
        </div>
      )}
    </div>
  );
};

export default SquadsStep;